import { twMerge } from 'tailwind-merge';
import cx from 'classnames';

import Spinner, { type SpinnerProps } from './Spinner';

export default function Button({
  children,
  buttonProps,
  variant = 'filled',
  size = 'big',
  startIcon,
  classes,
  loading = false,
  loadingProps,
}: React.PropsWithChildren<{
  variant?: 'filled' | 'outlined';
  size?: 'small' | 'big';
  buttonProps?: React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >;
  startIcon?: React.ReactNode;
  classes?: {
    startIcon?: string;
    children?: string;
  };
  loading?: boolean;
  loadingProps?: SpinnerProps;
}>): React.ReactElement {
  return (
    <button
      {...buttonProps}
      className={twMerge(
        'flex items-center gap-2 w-fit font-semibold disabled:text-neutral-30 disabled:pointer-events-none disabled:select-none leading-4 focus:outline-none',
        cx({
          'rounded-2xl px-4 py-2 h-8 max-h-8': size === 'small',
          'rounded-3xl px-6 py-4 h-12 max-h-12': size === 'big',
          'bg-primary-10 text-neutral-100 hover:bg-primary-30 active:bg-primary-10 active:brightness-75 disabled:bg-neutral-90':
            variant === 'filled',
          'border border-primary-10 text-primary-10 hover:border-primary-30 hover:text-primary-30 hover:bg-primary-97 active:brightness-75 disabled:border-neutral-70':
            variant === 'outlined',
          'px-6 py-3': size === 'big' && startIcon && children,
          'p-3': size === 'big' && startIcon && !children,
        }),
        buttonProps?.className,
      )}
    >
      {loading && (
        <div className="w-full flex justify-center items-center">
          <Spinner
            className={loadingProps?.className}
            variant={loadingProps?.variant ?? (variant === 'filled' ? 'light' : 'dark')}
            size={loadingProps?.size ?? 'md'}
          />
        </div>
      )}
      {!loading && startIcon && (
        <div className={twMerge('h-6 w-6', classes?.startIcon)}>{startIcon}</div>
      )}
      {!loading && children && (
        <div className={twMerge('w-full', classes?.children)}>{children}</div>
      )}
    </button>
  );
}
